import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Acceptance of terms",
    body: (
      <>
        <p>
          By accessing or using ResumeLab (the &ldquo;Service&rdquo;) you agree to be bound by
          these Terms of Service (&ldquo;Terms&rdquo;). If you don't agree, don't use the Service.
          We may update these Terms at any time and will post the latest version at{" "}
          <Link to="/terms" className="text-[#002FA7] underline-offset-4 hover:underline">/terms</Link>.
          Continued use after a change means you accept the update.
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "What ResumeLab does",
    body: (
      <>
        <p>
          ResumeLab is an AI-powered resume evaluation and rewriting service. We score resumes,
          generate ATS-compatibility analyses, AI rewrites, cover letters, and provide
          subscription-based access to advanced features.
        </p>
        <p>
          <b>The Service is informational only.</b> It does not guarantee job offers, interviews,
          or any specific career outcome.
        </p>
      </>
    ),
  },
  {
    n: "03",
    title: "Your account",
    body: (
      <>
        <p>
          You may use ResumeLab anonymously (one free audit per month per IP) or sign in via Google
          OAuth. You're responsible for the security of your Google account and any activity that
          happens through it. We don't store your Google password.
        </p>
      </>
    ),
  },
  {
    n: "04",
    title: "Subscriptions & billing",
    body: (
      <>
        <p>
          <b>ResumeLab Pro</b> is a recurring monthly subscription billed at the price shown on
          the <Link to="/pricing" className="text-[#002FA7] underline-offset-4 hover:underline">Pricing</Link>{" "}
          page (currently US$20 / month). Subscriptions auto-renew at the end of each billing
          period unless canceled.
        </p>
        <p>
          <b>Cancellation:</b> You can cancel anytime from the Stripe Billing Portal accessible
          from <Link to="/pricing" className="text-[#002FA7] underline-offset-4 hover:underline">/pricing</Link>{" "}
          once subscribed. Cancellation takes effect at the end of the current billing period —
          you retain Pro access until then.
        </p>
        <p>
          <b>Refunds:</b> All sales are final. We may, at our sole discretion, issue refunds for
          billing errors or service outages. Contact{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>.
        </p>
        <p>
          <b>Promotional codes</b> (e.g. <code>SAVE30</code>) are one-time use per customer unless
          otherwise stated.
        </p>
        <p>
          Payments are processed by <b>Stripe, Inc.</b> We never see or store your full card
          number. By subscribing you also agree to{" "}
          <a href="https://stripe.com/legal/consumer" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline-offset-4 hover:underline">
            Stripe's terms
          </a>.
        </p>
      </>
    ),
  },
  {
    n: "05",
    title: "Your content",
    body: (
      <>
        <p>
          You retain ownership of the resume text, LinkedIn copy, and other content you submit
          (&ldquo;Your Content&rdquo;). You grant ResumeLab a limited license to process Your Content
          in order to run audits, generate rewrites, store history, and improve the Service.
        </p>
        <p>
          <b>You are responsible</b> for ensuring Your Content does not infringe third-party rights
          or contain unlawful information. Don't upload other people's resumes without consent.
        </p>
      </>
    ),
  },
  {
    n: "06",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree NOT to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Reverse-engineer, scrape, or copy the Service for a competing product</li>
          <li>Submit malware, illegal content, or content that violates third-party rights</li>
          <li>Use the Service to generate misleading or fraudulent resumes</li>
          <li>Bypass rate limits, authentication, or payment controls</li>
          <li>Resell access to ResumeLab without a written agreement</li>
        </ul>
      </>
    ),
  },
  {
    n: "07",
    title: "AI & accuracy",
    body: (
      <>
        <p>
          Audits, scores, and rewrites are produced by large language models (currently OpenAI
          GPT&#8209;5.2 via the Emergent platform). LLMs can make mistakes, miss context, or produce
          inconsistent output. <b>Review every rewrite before using it.</b> ResumeLab is not
          responsible for content you submit to employers based on AI output.
        </p>
      </>
    ),
  },
  {
    n: "08",
    title: "Service availability",
    body: (
      <>
        <p>
          We aim for high uptime but don't guarantee uninterrupted access. We may suspend the
          Service for maintenance, upgrades, or in response to abuse. We're not liable for
          downtime or data loss caused by third-party providers (Stripe, Resend, OpenAI, the
          hosting platform).
        </p>
      </>
    ),
  },
  {
    n: "09",
    title: "Termination",
    body: (
      <>
        <p>
          We may suspend or terminate accounts that violate these Terms or applicable law. You may
          delete your account at any time by contacting{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>{" "}
          — we'll delete your audits, cover letters, and personal data within 30 days unless
          retention is required by law.
        </p>
      </>
    ),
  },
  {
    n: "10",
    title: "Disclaimer of warranties",
    body: (
      <>
        <p className="uppercase tracking-wide text-xs text-zinc-500 mb-2">In capital letters because the lawyers said so:</p>
        <p>
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY
          KIND, EXPRESS OR IMPLIED. RESUMELAB DISCLAIMS ALL WARRANTIES INCLUDING MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
      </>
    ),
  },
  {
    n: "11",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, RESUMELAB'S TOTAL LIABILITY FOR ANY CLAIM
          ARISING FROM THE SERVICE WILL NOT EXCEED THE GREATER OF (a) US$50 OR (b) THE FEES YOU
          PAID TO RESUMELAB IN THE 12 MONTHS BEFORE THE CLAIM.
        </p>
      </>
    ),
  },
  {
    n: "12",
    title: "Governing law",
    body: (
      <>
        <p>
          These Terms are governed by the laws of <b>Kenya</b>, without regard to conflict-of-laws
          rules. Any dispute will be resolved in the courts of Nairobi, Kenya, unless mandatory
          consumer-protection law in your jurisdiction requires otherwise.
        </p>
      </>
    ),
  },
  {
    n: "13",
    title: "Contact",
    body: (
      <>
        <p>
          Questions? Email{" "}
          <a href="mailto:hello@resumelab.co.ke" className="text-[#002FA7] underline-offset-4 hover:underline">
            hello@resumelab.co.ke
          </a>.
        </p>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="terms-page">
      <SEO
        title="Terms of Service"
        description="ResumeLab Terms of Service — subscription, cancellation, content ownership, and Kenyan-law jurisdiction."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-14">
        <div className="overline mb-4">◆ LEGAL · TERMS OF SERVICE</div>
        <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight leading-[0.95] mb-6">
          Terms of<br />Service.
        </h1>
        <p className="text-zinc-500 mb-2">Last updated: <span className="font-mono">May 22, 2026</span></p>
        <p className="text-zinc-500 mb-12">
          Plain-English summary up front, full terms below. By using ResumeLab you agree to these
          Terms — please read them.
        </p>

        <div className="bg-zinc-50 border border-zinc-200/70 p-6 lg:p-8 mb-12">
          <div className="overline mb-3">◇ THE SHORT VERSION</div>
          <ul className="space-y-2 text-sm text-zinc-700">
            <li>• You use ResumeLab at your own risk. We're not your career counselor or lawyer.</li>
            <li>• Pro is US$20 / month, auto-renews, cancel anytime from the billing portal.</li>
            <li>• You keep ownership of your resume. We use it only to power your audits.</li>
            <li>• Don't upload other people's data, malware, or anything illegal.</li>
            <li>• AI can be wrong. Review every rewrite before sending it to a recruiter.</li>
          </ul>
        </div>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.n} data-testid={`terms-section-${s.n}`}>
              <div className="font-mono text-xs text-zinc-400 mb-2">{s.n} / {String(SECTIONS.length).padStart(2, "0")}</div>
              <h2 className="font-display font-extrabold text-2xl mb-4">{s.title}</h2>
              <div className="text-zinc-700 leading-relaxed space-y-3 text-[15px]">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          See also: <Link to="/privacy" className="text-[#002FA7] underline-offset-4 hover:underline">Privacy Policy</Link>.
        </div>
      </div>
      <Footer />
    </main>
  );
}
